import { ScheduleRecordProperty, UUID } from '@indigohive/cogfy-types'
import { GetCollectionFieldsResultData } from '@indigohive/cogfy-types/endpoints/getCollectionFields'
import { GetRecordByIdResult } from '@indigohive/cogfy-types/endpoints/getRecordById'
import { UpdateRecordPropertiesOperationData } from '@indigohive/cogfy-types/endpoints/transactions'
import { Button, Input, Select } from '../../../components'
import { formatDate } from 'date-fns/format'
import { isPast } from 'date-fns/isPast'
import { useEffect, useState } from 'react'
import { isValid } from 'date-fns'

type Unit = 'hours' | 'days' | 'weeks' | 'months'

type ScheduleRecordPropertyInputProps = {
  collectionId: UUID
  field: Extract<GetCollectionFieldsResultData, { type: 'schedule' }>
  record: GetRecordByIdResult
  onUpdateRecordProperties?: (data: UpdateRecordPropertiesOperationData) => void
}

export function ScheduleRecordPropertyInput (props: ScheduleRecordPropertyInputProps) {
  const { collectionId, field, record, onUpdateRecordProperties } = props

  const property = record.properties[field.id] as ScheduleRecordProperty | undefined
  const value = property?.schedule?.value

  const [startDateInput, setStartDateInput] = useState<string | null>(
    value?.startDate ? formatDate(new Date(value.startDate), "yyyy-MM-dd'T'HH:mm") : null
  )
  const startDateInputDate = startDateInput ? new Date(startDateInput) : null
  const [endDateInput, setEndDateInput] = useState<string | null>(
    value?.endDate ? formatDate(new Date(value.endDate), "yyyy-MM-dd'T'HH:mm") : null
  )

  const [interval, setInterval] = useState<number | null>(value?.interval ?? null)
  const [unit, setUnit] = useState<Unit | null>(value?.unit as Unit ?? null)
  const [maxOccurrences, setMaxOccurrences] = useState<number | null>(value?.maxOccurrences ?? null)

  const startDateError = startDateInputDate
    ? (isValid(startDateInputDate) && isPast(startDateInputDate))
    : false
  const endDateError = endDateInput
    ? (isValid(new Date(endDateInput)) && isPast(new Date(endDateInput)))
    : false
  const intervalError = interval !== null && (interval < 1 || interval > 99)
  const maxOccurrencesError = maxOccurrences !== null && (maxOccurrences < 1)

  useEffect(() => {
    if (startDateInput && interval && unit && !startDateError && !intervalError && !endDateError) {
      const startDate = new Date(startDateInput)
      const endDate = endDateInput ? new Date(endDateInput) : null

      if (isValid(startDate) && (!endDate || isValid(endDate))) {
        onUpdateRecordProperties?.({
          collectionId,
          recordId: props.record.id,
          properties: {
            [props.field.id]: {
              type: 'schedule',
              schedule: {
                value: {
                  startDate: startDate.toISOString(),
                  nextDate: startDate.toISOString(),
                  interval,
                  unit,
                  endDate: endDate?.toISOString() ?? null,
                  maxOccurrences,
                  count: value?.count ?? 0
                }
              }
            }
          }
        })
      }
    }
  }, [startDateInput, endDateInput, interval, unit, maxOccurrences, startDateError, intervalError, endDateError])

  const handleClear = () => {
    setStartDateInput(null)
    setInterval(null)
    setUnit(null)
    onUpdateRecordProperties?.({
      collectionId,
      recordId: props.record.id,
      properties: {
        [props.field.id]: {
          type: 'schedule',
          schedule: {
            value: null
          }
        }
      }
    })
  }

  return (
    <div className="border rounded-sm max-w-full flex flex-col gap-4 px-2 py-2">
      <div className="flex w-full gap-4 h-fit">

        <div className="basis-1/2">
          <Input
            size="sm"
            disabled={!!value?.startDate}
            error={startDateError && !value?.startDate}
            helperText={!value?.startDate && startDateError ? 'Start date must be in the future' : ''}
            value={startDateInput ?? ''}
            onChange={event => {
              setStartDateInput(event.target.value)
            }}
            label="Start date"
            required
            type="datetime-local"
          />
        </div>

        <div className="basis-1/2">
          <div className="flex gap-2 items-center">
            <Input
              size="sm"
              label="End date"
              error={endDateError}
              helperText={endDateError ? 'End date must be in the future' : ''}
              value={endDateInput ?? ''}
              onChange={event => {
                setEndDateInput(event.target.value)
              }}
              type="datetime-local"
            />
          </div>
        </div>
      </div>

      <div className="flex w-full gap-4 h-fit">
        <div className="basis-1/2">
          <div className="flex gap-2">
            <Select
              size="md"
              value={unit || ''}
              onChange={event => {
                setUnit(event.target.value as Unit)
              }}
              options={[
                { label: '-', value: '' },
                { label: 'Hours', value: 'hours' },
                { label: 'Days', value: 'days' },
                { label: 'Weeks', value: 'weeks' },
                { label: 'Months', value: 'months' }
              ]}
              label="Interval"
              required
            />
            <Input
              size="sm"
              value={interval ?? ''}
              error={intervalError}
              helperText={intervalError ? 'Interval must be between 1 and 99' : undefined}
              onChange={event => {
                setInterval(Number(event.target.value) || null)
              }}
              label="&nbsp;"
              type="number"
              min={1}
              max={99}
            />
          </div>
        </div>
        <div className="basis-1/2">
          <div className="flex gap-2">
            <Input
              size="sm"
              value={maxOccurrences ?? ''}
              error={maxOccurrencesError}
              helperText={maxOccurrencesError ? 'Max occurrences must be greater than 0' : ''}
              onChange={event => {
                setMaxOccurrences(Number(event.target.value))
              }}
              label="Max occurences"
              type="number"
              min={1}
            />
          </div>
        </div>
      </div>
      <Button
        size="xs"
        onClick={() => handleClear()}
      >
        Clear
      </Button>
    </div>
  )
}
